import {
  getGetTravellerBearersQueryKey,
  TravellerWithCollectionMembershipsDTO,
  useLinkTravellerBobcatAccount,
  useListBearers,
  useUpdateTravellerByID,
  useIsTravellerEmailAvailable,
} from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { useIntl } from 'react-intl';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FormElement } from '../FormElement';
import { FormSection } from '../FormSection';
import { FormSubmitActions } from '../FormSubmitActions';
import {
  getApiErrorMessage,
  isResourceNotFoundError,
} from '@/helpers/apiError';
import { blankToUndefined, trimData } from '@/helpers/trimData';
import { normalizeCareOf } from '@/helpers/careOf';
import { streetSchema } from '@/helpers/address';
import { TravellerDTO } from '@/api/trafikportalen';
import { zodResolver } from '@hookform/resolvers/zod';
import { updateTravellerByIDBody } from '@/api/trafikportalen/trafikportalenAPI.zod';
import { toast } from '@/hooks/useToast';
import { getGetTravellerByIDQueryKey } from '@/api/trafikportalen';
import { z } from 'zod';
import { InputText } from '../Inputs/InputText';
import { InputPhone } from '../Inputs/InputPhone';
import { InputPostalCode } from '../Inputs/InputPostalCode';
import { formatDateTime } from '@/helpers/formatting';
import { hasAnyMembershipDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { MOBILE_PHONE_REGEX, isValidEmail } from '@/helpers/validation';
import { FieldError } from 'react-hook-form';
import { WarningBanner } from '../core/WarningBanner';

type TravellerFormType = z.infer<typeof updateTravellerByIDBody> & {
  bobcat_private_email?: string;
};

export const ContactInformationForm = ({
  traveller,
}: {
  traveller: TravellerWithCollectionMembershipsDTO;
}) => {
  const [profileValues, setProfileValues] = useState<TravellerFormType>({
    ...traveller.traveller,
  });
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const [isEditMode, setIsEditMode] = useState(false);

  const updateTravellerByID = useUpdateTravellerByID();
  const linkTravellerBobcatAccount = useLinkTravellerBobcatAccount();
  const queryClient = useQueryClient();

  const customErrorMap = useCustomErrorMap;
  const { formatMessage } = useIntl();

  const hasNonSchoolMemberships = useMemo(() => {
    return traveller.memberships.some(
      ({ collection_type }) => collection_type === 'master',
    );
  }, [traveller.memberships]);

  const hasSchoolMemberships = useMemo(() => {
    return traveller.memberships.some(({ collection_type }) =>
      collection_type?.startsWith('school-'),
    );
  }, [traveller.memberships]);

  const customSchema = useMemo(() => {
    const schema = updateTravellerByIDBody.extend({
      phone: z
        .string()
        .regex(MOBILE_PHONE_REGEX, {
          message: 'Telefonnummer måste vara + följt av exakt 11 siffror',
        })
        .optional()
        .or(z.literal('')),
      email: hasNonSchoolMemberships
        ? z.string().email()
        : z.string().email().optional().or(z.literal('')),
      bobcat_private_email: z.string().email().optional().or(z.literal('')),
    });

    if (!hasSchoolMemberships) return schema;

    return schema.and(
      z.object({
        delivery_address: z
          .object({
            street: streetSchema(formatMessage, { hasCareOfField: true }),
          })
          .optional(),
      }),
    );
  }, [hasNonSchoolMemberships, hasSchoolMemberships, formatMessage]);

  const customResolver = zodResolver(customSchema, {
    errorMap: customErrorMap(),
  });

  const methods = useForm<TravellerFormType>({
    defaultValues: {
      ...profileValues,
    },
    resolver: async (values, context, options) => {
      const processedValues = { ...values };
      if (!hasNonSchoolMemberships && processedValues.email === '') {
        processedValues.email = undefined;
      }
      if (processedValues.bobcat_private_email === '') {
        processedValues.bobcat_private_email = undefined;
      }
      if (processedValues.phone === '') {
        processedValues.phone = undefined;
      }
      if (processedValues.national_identity_number === '') {
        processedValues.national_identity_number = undefined;
      }
      if (
        typeof processedValues.national_identity_number === 'string' &&
        !processedValues.national_identity_number.includes('-')
      ) {
        const nin = processedValues.national_identity_number;
        if (nin.length >= 9) {
          processedValues.national_identity_number =
            nin.slice(0, 8) + '-' + nin.slice(8);
        }
      }
      if (processedValues.delivery_address) {
        processedValues.delivery_address = {
          ...processedValues.delivery_address,
          care_of: normalizeCareOf(processedValues.delivery_address.care_of),
        };
      }
      const hasAddr =
        processedValues.delivery_address?.street ||
        processedValues.delivery_address?.zip_code ||
        processedValues.delivery_address?.city ||
        processedValues.delivery_address?.care_of;
      if (!hasAddr) {
        processedValues.delivery_address = undefined;
      }
      return customResolver(processedValues, context, options);
    },
    mode: 'all',
  });
  const { handleSubmit, control, formState, watch } = methods;

  const watchedEmail = watch('email');
  const watchedBobcatEmail = watch('bobcat_private_email');
  const watchedCareOf = watch('delivery_address.care_of');
  const [debouncedBobcatEmail, setDebouncedBobcatEmail] = useState(
    watchedBobcatEmail ?? '',
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBobcatEmail(watchedBobcatEmail ?? '');
    }, 300);

    return () => clearTimeout(timer);
  }, [watchedBobcatEmail]);

  const emailChanged = Boolean(
    watchedEmail &&
    watchedEmail.toLowerCase() !== traveller.traveller.email?.toLowerCase(),
  );

  const emailAvailableQueryEnabled = Boolean(
    isEditMode &&
    emailChanged &&
    watchedEmail &&
    watchedEmail.trim() !== '' &&
    isValidEmail(watchedEmail),
  );

  const {
    data: emailAvailability,
    isLoading: emailCheckLoading,
    isFetching: emailCheckFetching,
  } = useIsTravellerEmailAvailable(
    { email: watchedEmail || '' },
    {
      query: {
        enabled: emailAvailableQueryEnabled,
      },
    },
  );

  const emailTaken = emailChanged && emailAvailability?.available === false;

  const normalizedBobcatEmail = debouncedBobcatEmail.trim().toLowerCase();
  const currentBobcatEmail =
    traveller.traveller.bobcat_private_email?.toLowerCase() ?? '';
  const bobcatEmailLookupEnabled = Boolean(
    isEditMode &&
    normalizedBobcatEmail &&
    normalizedBobcatEmail !== currentBobcatEmail &&
    isValidEmail(normalizedBobcatEmail),
  );

  const {
    error: bobcatAccountLookupError,
    isError: bobcatAccountLookupIsError,
    isSuccess: bobcatAccountLookupSuccess,
    isLoading: bobcatAccountLookupLoading,
    isFetching: bobcatAccountLookupFetching,
  } = useListBearers(
    { email: normalizedBobcatEmail },
    {
      query: {
        enabled: bobcatEmailLookupEnabled,
        retry: false,
      },
    },
  );

  const bobcatAccountLookupStatus = isAxiosError(bobcatAccountLookupError)
    ? bobcatAccountLookupError.response?.status
    : undefined;
  const bobcatAccountNotFound =
    bobcatAccountLookupStatus === 500 ||
    isResourceNotFoundError(bobcatAccountLookupError);

  const bobcatAccountMissing =
    bobcatEmailLookupEnabled &&
    !bobcatAccountLookupLoading &&
    !bobcatAccountLookupFetching &&
    bobcatAccountNotFound;

  const bobcatAccountLookupFailed =
    bobcatEmailLookupEnabled &&
    !bobcatAccountLookupLoading &&
    !bobcatAccountLookupFetching &&
    bobcatAccountLookupIsError &&
    !bobcatAccountNotFound;

  const bobcatAccountNotFoundMessage = formatMessage({
    defaultMessage: 'Det finns inget konto i reseappen med denna e-postadress.',
  });
  const bobcatAccountLookupFailedMessage = formatMessage({
    defaultMessage: 'Kunde inte kontrollera kontot i reseappen.',
  });

  const startEdit = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const cancelEdit = useCallback(
    (updatedTraveller: TravellerDTO) => {
      methods.reset({
        ...updatedTraveller,
        bobcat_private_email: updatedTraveller.bobcat_private_email ?? '',
      });
      setIsEditMode(false);
    },
    [methods],
  );

  const onSubmit: SubmitHandler<TravellerFormType> = async (data) => {
    const trimmedData = trimData({
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
      bobcat_private_email: data.bobcat_private_email,
      national_identity_number: data.national_identity_number,
      delivery_address: data.delivery_address,
    });

    const hasDeliveryAddress =
      trimmedData.delivery_address?.street ||
      trimmedData.delivery_address?.zip_code ||
      trimmedData.delivery_address?.city;

    const nextBobcatPrivateEmail = blankToUndefined(
      trimmedData.bobcat_private_email,
    );

    const processedData = {
      email: hasNonSchoolMemberships
        ? trimmedData.email
        : blankToUndefined(trimmedData.email),
      first_name: trimmedData.first_name,
      last_name: trimmedData.last_name,
      phone: blankToUndefined(trimmedData.phone),
      national_identity_number: blankToUndefined(
        trimmedData.national_identity_number,
      ),
      delivery_address: hasDeliveryAddress
        ? trimmedData.delivery_address
        : undefined,
      bobcat_private_email: nextBobcatPrivateEmail,
    };

    setProfileValues(processedData);

    const { bobcat_private_email: bobcatPrivateEmail, ...travellerUpdate } =
      processedData;

    const currentLinkedEmail =
      traveller.traveller.bobcat_private_email?.toLowerCase() ?? '';
    const nextBobcatEmail = bobcatPrivateEmail?.toLowerCase() ?? '';
    const bobcatEmailChanged = nextBobcatEmail !== currentLinkedEmail;

    if (nextBobcatEmail && bobcatEmailChanged) {
      if (bobcatAccountMissing) {
        toast.error(
          {
            title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            description: bobcatAccountNotFoundMessage,
          },
          { autoClose: 3000 },
        );
        return;
      }

      if (bobcatAccountLookupFailed) {
        toast.error(
          {
            title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            description: bobcatAccountLookupFailedMessage,
          },
          { autoClose: 3000 },
        );
        return;
      }
    }

    try {
      await updateTravellerByID.mutateAsync({
        travellerID: traveller.traveller.id,
        data: travellerUpdate,
      });

      let nextTraveller = traveller.traveller;

      if (bobcatEmailChanged) {
        const linkedTraveller = await linkTravellerBobcatAccount.mutateAsync({
          travellerID: traveller.traveller.id,
          data: { bobcat_private_email: bobcatPrivateEmail },
        });
        nextTraveller = {
          ...linkedTraveller,
          bobcat_private_email: bobcatPrivateEmail,
        };
      }

      toast.success(
        {
          title: formatMessage({ defaultMessage: 'Resenär uppdaterad' }),
        },
        { autoClose: 3000 },
      );

      const travellerQueryKey = getGetTravellerByIDQueryKey(
        traveller.traveller.id,
      );

      queryClient.setQueryData<TravellerWithCollectionMembershipsDTO>(
        travellerQueryKey,
        (current) =>
          current
            ? {
                ...current,
                traveller: {
                  ...current.traveller,
                  ...nextTraveller,
                  bobcat_private_email: bobcatPrivateEmail,
                },
              }
            : current,
      );

      await Promise.all([
        queryClient.refetchQueries({
          queryKey: travellerQueryKey,
        }),
        queryClient.refetchQueries({
          queryKey: getGetTravellerBearersQueryKey(traveller.traveller.id),
        }),
      ]);

      const freshTravellerData =
        queryClient.getQueryData<TravellerWithCollectionMembershipsDTO>(
          travellerQueryKey,
        );
      const resolvedTraveller = {
        ...(freshTravellerData?.traveller ?? nextTraveller),
        bobcat_private_email: bobcatEmailChanged
          ? bobcatPrivateEmail
          : (freshTravellerData?.traveller.bobcat_private_email ??
            nextTraveller.bobcat_private_email),
      };

      queryClient.setQueryData<TravellerWithCollectionMembershipsDTO>(
        travellerQueryKey,
        (current) =>
          current
            ? {
                ...current,
                traveller: { ...current.traveller, ...resolvedTraveller },
              }
            : current,
      );

      cancelEdit(resolvedTraveller);
    } catch (error) {
      toast.error(
        {
          title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
          description: isResourceNotFoundError(error)
            ? bobcatAccountNotFoundMessage
            : getApiErrorMessage(
                error,
                formatMessage({ defaultMessage: 'Ett fel inträffade' }),
              ),
        },
        { autoClose: 3000 },
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} ref={ref}>
        <FormSection
          title={<FormattedMessage defaultMessage="Allmän information" />}
          editMode={isEditMode}
          canSeeButton={hasAnyMembershipDepartmentWriteAccess(traveller)}
          onClickButton={startEdit}
          className={hasSchoolMemberships ? 'rounded-b-none' : undefined}
        >
          {hasSchoolMemberships && isEditMode && (
            <WarningBanner>
              <FormattedMessage defaultMessage="Denna resenären är med i en eller flera skolavdelningar." />{' '}
              <FormattedMessage
                id="page.students.manualEditWarning"
                defaultMessage="Manuell redigering av personer i en elevkategori kommer bli överskrivet vid nästa import om inte samma ändringar finns i importfilen."
              />
              <FormattedMessage defaultMessage="För ändringar på saker som är unikt för elevkategorin kommer det bli en varning på personen, men det gäller inte för informationen i denna vyn." />
            </WarningBanner>
          )}
          <div className="flex flex-col gap-2">
            <FormElement
              id="email"
              label={<FormattedMessage defaultMessage="E-post" />}
              required={isEditMode && hasNonSchoolMemberships}
              input={
                <InputText
                  name="email"
                  control={control}
                  editMode={isEditMode}
                  error={
                    formState.errors.email ||
                    (emailTaken
                      ? {
                          message: formatMessage({
                            defaultMessage:
                              'En resenär med denna e-postadress finns redan i organisationen.',
                          }),
                          type: 'custom',
                        }
                      : undefined)
                  }
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="first_name"
              label={<FormattedMessage defaultMessage="Förnamn" />}
              input={
                <InputText
                  name="first_name"
                  control={control}
                  editMode={isEditMode}
                  error={formState.errors.first_name}
                />
              }
            />
            <FormElement
              id="last_name"
              label={<FormattedMessage defaultMessage="Efternamn" />}
              input={
                <InputText
                  name="last_name"
                  control={control}
                  editMode={isEditMode}
                  error={formState.errors.last_name}
                />
              }
            />
            <FormElement
              id="national_identity_number"
              label={<FormattedMessage defaultMessage="Personnummer" />}
              input={
                <InputText
                  name="national_identity_number"
                  control={control}
                  editMode={isEditMode}
                  error={formState.errors.national_identity_number}
                  placeholder={formatMessage({
                    defaultMessage: 'YYYYMMDD-XXXX',
                  })}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="bobcat_private_email"
              label={
                <FormattedMessage defaultMessage="E-post i appen / Mina sidor" />
              }
              input={
                <InputText
                  name="bobcat_private_email"
                  control={control}
                  editMode={isEditMode}
                  error={
                    formState.errors.bobcat_private_email ||
                    (bobcatAccountMissing
                      ? {
                          message: bobcatAccountNotFoundMessage,
                          type: 'custom',
                        }
                      : bobcatAccountLookupFailed
                        ? {
                            message: bobcatAccountLookupFailedMessage,
                            type: 'custom',
                          }
                        : undefined)
                  }
                  placeholder="hello@example.com"
                  helperText={
                    bobcatAccountLookupSuccess && bobcatEmailLookupEnabled
                      ? formatMessage({
                          defaultMessage: 'Kontot finns i reseappen.',
                        })
                      : undefined
                  }
                  helperTextReserveSpace
                />
              }
            />
            {hasNonSchoolMemberships && (
              <FormElement
                id="phone"
                label={<FormattedMessage defaultMessage="Telefonnummer" />}
                input={
                  <InputPhone
                    name="phone"
                    control={control}
                    editMode={isEditMode}
                    error={formState.errors.phone}
                    helperTextReserveSpace
                  />
                }
              />
            )}

            <i className="mt-3 text-text-700">
              <FormattedMessage defaultMessage="Kontaktinformationen är gemensam för resenären i alla avdelningar hen är med i." />
              {hasNonSchoolMemberships && (
                <>
                  <br />
                  <FormattedMessage defaultMessage="Telefonnummer används enbart för sms-utskick vid tilldelning av tjänsteresa i app." />
                </>
              )}
            </i>

            <div className="flex flex-wrap gap-x-10 text-sm text-text-700">
              <p>
                <FormattedMessage
                  defaultMessage="Skapad {date}"
                  values={{
                    date: formatDateTime(traveller.traveller.created_at),
                  }}
                />
              </p>
              <p>
                <FormattedMessage
                  defaultMessage="Uppdaterad {date}"
                  values={{
                    date: formatDateTime(traveller.traveller.updated_at),
                  }}
                />
              </p>
            </div>
          </div>
        </FormSection>
        {hasSchoolMemberships && (
          <FormSection
            title={<FormattedMessage defaultMessage="Leveransadress" />}
            editMode={isEditMode}
            className="-mt-px rounded-t-none"
          >
            <div className="flex flex-col gap-2">
              <i className="text-text-700">
                <FormattedMessage defaultMessage="Leveransadressen används för att skicka ut fysiska kort om det beställs. Bara aktuellt för barn och ungdomar." />
              </i>
              <FormElement
                label={<FormattedMessage defaultMessage="c/o" />}
                visible={isEditMode || !!watchedCareOf}
                input={
                  <InputText
                    name={'delivery_address.care_of' as never}
                    control={control}
                    editMode={isEditMode}
                    error={
                      formState.errors.delivery_address?.care_of as
                        | FieldError
                        | undefined
                    }
                    placeholder={formatMessage({ defaultMessage: 'Namn' })}
                    helperTextReserveSpace
                  />
                }
              />
              <FormElement
                label={<FormattedMessage defaultMessage="Adress" />}
                input={
                  <InputText
                    name={'delivery_address.street' as never}
                    control={control}
                    editMode={isEditMode}
                    error={
                      formState.errors.delivery_address?.street as
                        | FieldError
                        | undefined
                    }
                    helperTextReserveSpace
                  />
                }
              />
              <FormElement
                label={<FormattedMessage defaultMessage="Postnummer" />}
                input={
                  <InputPostalCode
                    name={'delivery_address.zip_code' as never}
                    control={control}
                    editMode={isEditMode}
                    error={
                      formState.errors.delivery_address?.zip_code as
                        | FieldError
                        | undefined
                    }
                    helperTextReserveSpace
                  />
                }
              />
              <FormElement
                label={<FormattedMessage defaultMessage="Postort" />}
                input={
                  <InputText
                    name={'delivery_address.city' as never}
                    control={control}
                    editMode={isEditMode}
                    error={
                      formState.errors.delivery_address?.city as
                        | FieldError
                        | undefined
                    }
                    helperTextReserveSpace
                  />
                }
              />
            </div>
          </FormSection>
        )}
        <FormSubmitActions
          active={isEditMode}
          createMode={false}
          onCancel={() => cancelEdit(traveller.traveller)}
          disabled={
            emailTaken ||
            emailCheckLoading ||
            emailCheckFetching ||
            bobcatAccountMissing ||
            bobcatAccountLookupFailed ||
            bobcatAccountLookupLoading ||
            bobcatAccountLookupFetching
          }
          isLoading={
            emailCheckLoading ||
            emailCheckFetching ||
            bobcatAccountLookupLoading ||
            bobcatAccountLookupFetching ||
            updateTravellerByID.isPending ||
            linkTravellerBobcatAccount.isPending
          }
        />
      </form>
    </FormProvider>
  );
};
